import { type NodePosition } from '../../hooks/commands/types';
import { type AppNode } from '../../types';
import { type NodeRect, toNodeRect } from '../layout/nodeLayout';
import { computeGroupBounds } from './nodeGrouping';

export type GroupAwareRects = {
  layoutRects: NodeRect[];
  memberMap: Map<string, AppNode[]>;
};

const GROUP_ID_PREFIX = 'group:';

/**
 * Collapses grouped nodes into single synthetic rects for layout calculations.
 * Ungrouped nodes are converted to individual rects; each group becomes one rect
 * representing its bounding box (id prefixed with "group:").
 */
export function buildGroupAwareRects(selectedNodes: AppNode[]): GroupAwareRects {
  const memberMap = new Map<string, AppNode[]>();
  const ungrouped: AppNode[] = [];

  for (const node of selectedNodes) {
    if (node.groupId) {
      let members = memberMap.get(node.groupId);

      if (!members) {
        members = [];
        memberMap.set(node.groupId, members);
      }

      members.push(node);
    } else {
      ungrouped.push(node);
    }
  }

  const layoutRects: NodeRect[] = ungrouped.map(toNodeRect);

  for (const [groupId, members] of memberMap) {
    const bounds = computeGroupBounds(members);

    if (bounds) {
      layoutRects.push({
        height: bounds.height,
        id: `${GROUP_ID_PREFIX}${groupId}`,
        width: bounds.width,
        x: bounds.minX,
        y: bounds.minY,
      });
    }
  }

  return { layoutRects, memberMap };
}

/**
 * Expands layout positions back to individual node positions.
 * For group rects, computes the delta from the original rect position and
 * applies it to all member nodes, preserving their relative offsets.
 */
export function expandGroupPositions(
  layoutPositions: NodePosition[],
  layoutRects: NodeRect[],
  memberMap: Map<string, AppNode[]>,
): NodePosition[] {
  const result: NodePosition[] = [];

  for (const pos of layoutPositions) {
    if (pos.id.startsWith(GROUP_ID_PREFIX)) {
      const groupId = pos.id.slice(GROUP_ID_PREFIX.length);
      const members = memberMap.get(groupId);
      const originalRect = layoutRects.find((r) => r.id === pos.id);

      if (!members || !originalRect) {
        continue;
      }

      const deltaX = pos.position.x - originalRect.x;
      const deltaY = pos.position.y - originalRect.y;

      for (const member of members) {
        result.push({
          id: member.id,
          position: { x: member.position.x + deltaX, y: member.position.y + deltaY },
        });
      }
    } else {
      result.push(pos);
    }
  }

  return result;
}
